import {Component, OnInit, OnDestroy} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import * as Communication from "../communication/index";
import {ToolbarService} from "../shared/common/ToolbarEventService";
import {ToolbarEventPageEnum, ToolbarEvent} from "../shared/common/ToolbarEvent";

/**
 * Landing page for the account-deletion link sent by mail.
 *
 * Reached at account/delete-confirm?dcode=<code>, without a session: the one-time code in the
 * link is the credential, and someone clicking a link in their mail is quite likely signed out.
 * Opening the page records the deletion request; it erases nothing.
 */
@Component({
    selector: 'sd-delete-confirm',
    templateUrl: 'delete-confirm.component.html',
    styleUrls: ['delete-confirm.component.css'],
    providers: [Communication.UsersService],
    standalone: false
})
export class DeleteConfirmComponent implements OnInit, OnDestroy {

  subscriptions:Subscription[] = [];

  loading:boolean = true;
  confirmed:boolean = false;
  deletionDate:string = "";

  /**
   * Copy is resolved through translate.get rather than translate.instant: this page is reached by
   * a pasted link on a cold load, where the translation bundle may not have arrived yet and
   * instant() would return the key and never recover.
   */
  title:string = "";
  body:string = "";
  supportNote:string = "";
  abortNote:string = "";

  constructor(private usersApi:Communication.UsersService,
              private activatedRoute:ActivatedRoute,
              private toolbarService:ToolbarService,
              public translate:TranslateService)
  {
  }

  ngOnInit() {
    this.toolbarService.action(new ToolbarEvent(ToolbarEventPageEnum.NONE, "", null, "", "", "", "", "", ""));

    this.subscriptions.push(this.activatedRoute.queryParams.subscribe(
      (params) => {
        let code = params['dcode'];
        if (!code) {
          this.finish(false, "");
          return;
        }
        this.confirmDeletion(code);
      }));

    this.subscriptions.push(this.translate.onLangChange.subscribe(() => this.renderCopy()));
  }

  private confirmDeletion(code:string) {
    this.subscriptions.push(this.usersApi.usersDeletionRequestConfirmCodePost(code).subscribe(
      (response:Communication.DeletionState) => {
        this.finish(true, response && response.deletionDate ? response.deletionDate : "");
      },
      (err:any) => {
        console.log(err);
        this.finish(false, "");
      }
    ));
  }

  private finish(confirmed:boolean, deletionDate:string) {
    this.confirmed = confirmed;
    this.deletionDate = deletionDate;
    this.loading = false;
    this.renderCopy();
  }

  private renderCopy() {
    if (this.loading) { return; }

    if (!this.confirmed) {
      this.subscriptions.push(this.translate.get('DELETE_CONFIRM_LINK_INVALID').subscribe(
        (text:string) => {
          this.title = "";
          this.body = text;
          this.supportNote = "";
          this.abortNote = "";
        }));
      return;
    }

    let keys = ['DELETE_CONFIRM_PAGE_TITLE', 'DELETE_CONFIRM_PAGE_TEXT', 'DELETION_PENDING_SUPPORT', 'DELETE_CONFIRM_PAGE_ABORT'];
    this.subscriptions.push(this.translate.get(keys, {date: this.asDayMonthYear(this.deletionDate)}).subscribe(
      (translations:any) => {
        this.title = translations['DELETE_CONFIRM_PAGE_TITLE'];
        this.body = translations['DELETE_CONFIRM_PAGE_TEXT'];
        this.supportNote = translations['DELETION_PENDING_SUPPORT'];
        this.abortNote = translations['DELETE_CONFIRM_PAGE_ABORT'];
      }));
  }

  private asDayMonthYear(value:any):string {
    if (value === null || value === undefined || value === "") { return ""; }

    let parsed = (typeof value === "number" || !isNaN(Number(value))) ? new Date(Number(value)) : new Date(value);
    if (isNaN(parsed.getTime())) { return ""; }

    let day = ("0" + parsed.getDate()).slice(-2);
    let month = ("0" + (parsed.getMonth() + 1)).slice(-2);
    return day + "/" + month + "/" + parsed.getFullYear();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s:Subscription) => s.unsubscribe());
  }
}
